import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MessageCircle, Users, Star, Heart, Zap, 
  X, Phone, Mail, MoreHorizontal, Search, Home, ZoomIn, ZoomOut 
} from 'lucide-react';
import '../../styles/cosmicConnections.css';

// SearchModal component moved outside to prevent re-creation
const SearchModal = React.memo(({ currentTheme, onClose, onSearch, t, showSearch, nodes = [] }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  // Generate suggestions based on query
  const suggestions = useMemo(() => {
    if (!query.trim()) return [];
    
    const searchTerm = query.toLowerCase().trim();
    const allSuggestions = [];
    
    // First, add direct node matches (users and groups)
    nodes.forEach(node => {
      const name = node.name.toLowerCase();
      let priority = 0;
      
      if (name === searchTerm) priority = 3; // Exact match
      else if (name.startsWith(searchTerm)) priority = 2; // Starts with
      else if (name.includes(searchTerm)) priority = 1; // Contains
      
      if (priority > 0) {
        allSuggestions.push({
          ...node,
          displayName: node.name,
          searchPriority: priority,
          isGroupMember: false
        });
      }
    });
    
    // Then, add group member matches as separate suggestions
    nodes.forEach(groupNode => {
      if (groupNode.type === 'group' && groupNode.membersList) {
        groupNode.membersList.forEach(member => {
          const memberName = member.name.toLowerCase();
          let priority = 0;
          
          if (memberName === searchTerm) priority = 3; // Exact match
          else if (memberName.startsWith(searchTerm)) priority = 2; // Starts with
          else if (memberName.includes(searchTerm)) priority = 1; // Contains
          
          if (priority > 0) {
            allSuggestions.push({
              id: `member-${groupNode.id}-${member.id}`,
              name: member.name,
              displayName: `${member.name} (in ${groupNode.name})`,
              type: 'group-member',
              searchPriority: priority,
              isGroupMember: true,
              parentGroupId: groupNode.id,
              parentGroupName: groupNode.name,
              memberData: member
            });
          }
        });
      }
    });
    
    // Sort by priority: exact match > starts with > contains, then alphabetically
    return allSuggestions
      .sort((a, b) => {
        if (a.searchPriority !== b.searchPriority) {
          return b.searchPriority - a.searchPriority; // Higher priority first
        }
        return a.name.localeCompare(b.name); // Alphabetical for same priority
      })
      .slice(0, 8); // Limit to 8 suggestions
  }, [query, nodes]);

  const handleSearchSubmit = useCallback((queryOverride = null) => {
    const searchQuery = queryOverride || query.trim();
    if (searchQuery) {
      onSearch(searchQuery);
      setQuery(''); // Clear the search input after search
      onClose();
    }
  }, [query, onSearch, onClose]);

  const handleSuggestionClick = useCallback((suggestion) => {
    setQuery(suggestion.name);
    handleSearchSubmit(suggestion.name);
  }, [handleSearchSubmit]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      if (suggestions.length > 0 && selectedIndex >= 0 && selectedIndex < suggestions.length) {
        // Use selected suggestion
        handleSuggestionClick(suggestions[selectedIndex]);
      } else {
        // Use typed query
        handleSearchSubmit();
      }
    } else if (e.key === 'Escape') {
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    }
  }, [handleSearchSubmit, handleSuggestionClick, onClose, suggestions, selectedIndex]);

  // Reset selected index when suggestions change
  useEffect(() => {
    setSelectedIndex(0);
  }, [suggestions]);

  if (!showSearch) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <motion.div
        initial={{ y: -30, opacity: 0, scale: 0.95 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: -30, opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.15 }}
        className="bg-slate-800/95 backdrop-blur-xl rounded-2xl p-6 w-96 max-w-md shadow-2xl border border-white/20"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {/* Header */}
        <div className="text-center mb-6">
          <Search size={32} className="mx-auto mb-3 text-purple-400" />
          <h3 className="text-xl font-bold text-white">
            Search Connections
          </h3>
          <p className="text-sm text-gray-400 mt-1">
            Find people and groups in your network
          </p>
        </div>
        
        {/* Search Input */}
        <div className="relative">
          <div className="flex items-center space-x-3 mb-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                name="searchQuery"
                id="searchQuery"
                placeholder="Search connections..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyPress}
                className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-slate-800/50 border border-white/10 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all"
                autoComplete="off"
                autoFocus
              />
            </div>
            {/* Search Button */}
            <button
              type="button"
              onClick={() => handleSearchSubmit()}
              disabled={!query.trim()}
              className={`px-4 py-2.5 rounded-xl font-medium text-white transition-all ${
                query.trim() 
                  ? 'bg-gradient-to-r from-purple-500 to-indigo-500 hover:shadow-lg' 
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              <Search size={16} />
            </button>
            {/* Close button */}
            <button
              type="button"
              onClick={onClose}
              className="p-2.5 rounded-xl hover:bg-white/10 transition-colors"
            >
              <X size={16} className="text-white/70" />
            </button>
          </div>

          {/* Suggestions Dropdown */}
          {suggestions.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute top-full left-0 right-0 mt-1 bg-slate-700/95 backdrop-blur-xl rounded-xl border border-white/20 shadow-xl overflow-hidden z-10"
            >
              {suggestions.map((suggestion, index) => (
                <motion.button
                  key={suggestion.id}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className={`w-full text-left px-4 py-3 flex items-center space-x-3 transition-colors ${
                    index === selectedIndex 
                      ? 'bg-purple-500/20 border-l-2 border-purple-400' 
                      : 'hover:bg-white/10'
                  }`}
                  whileHover={{ backgroundColor: 'rgba(139, 92, 246, 0.1)' }}
                  whileTap={{ scale: 0.98 }}
                >                  {/* Avatar */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white ${
                    suggestion.type === 'group' 
                      ? 'bg-gradient-to-br from-amber-400 to-orange-500' 
                      : 'bg-gradient-to-br from-blue-500 to-purple-600'
                  }`}>
                    {suggestion.name.charAt(0).toUpperCase()}
                  </div>
                  
                  {/* Name and Type */}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">
                      {suggestion.name}
                    </p>
                    <p className="text-gray-400 text-xs">
                      {suggestion.type === 'group' 
                        ? `Group • ${suggestion.membersList?.length || 0} members`
                        : `${suggestion.isOnline ? 'Online' : 'Offline'} • Level ${Math.floor((suggestion.xp || 1000) / 200) + 1}`
                      }
                    </p>
                  </div>
                  
                  {/* Type indicator */}
                  <div className="text-gray-400">
                    {suggestion.type === 'group' ? <Users size={14} /> : <MessageCircle size={14} />}
                  </div>
                </motion.button>
              ))}
            </motion.div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
});

SearchModal.displayName = 'SearchModal';

const CosmicConnectionsMap = ({ connections, currentTheme, onUserClick, onAddConnection, ignoredUsers = [], t, modalOpen = false }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const imageCache = useRef(new Map()); // Cache for loaded images

  const [selectedNode, setSelectedNode] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [cameraOffset, setCameraOffset] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [nodes, setNodes] = useState([]);  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [lastTouchDistance, setLastTouchDistance] = useState(0);
  const [clickedNode, setClickedNode] = useState(null);
  const [dragThreshold] = useState(5); // Minimum drag distance to consider as drag

  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [isAnimationPaused, setIsAnimationPaused] = useState(false);
  const [focusedNode, setFocusedNode] = useState(null);

  // Initialize cosmic nodes layout
  useEffect(() => {
    initializeCosmicLayout();
  }, [connections]);

  // Prevent page scrolling
  useEffect(() => {
    // Disable body scroll when component mounts
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    
    return () => {
      // Re-enable body scroll when component unmounts
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    };
  }, []);

  // Re-enable canvas interactions when modal closes
  useEffect(() => {
    if (!modalOpen && canvasRef.current) {
      // Small delay to ensure modal is fully closed and drag state cleared
      const timer = setTimeout(() => {
        const canvas = canvasRef.current;
        if (canvas) {          // Reset all interaction state
          setIsDragging(false);
          setDragStart({ x: 0, y: 0 });
          setHoveredNode(null);
          
          // Clear any global touch/drag state that might be stuck
          document.body.style.touchAction = '';
          document.body.style.userSelect = '';
          
          // Force canvas to be interactable again
          canvas.style.pointerEvents = 'none';
          canvas.style.touchAction = 'none';
          canvas.style.userSelect = 'none';
          
          // Use multiple animation frames to ensure proper event restoration
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (canvas) {
                canvas.style.pointerEvents = 'auto';
                canvas.style.touchAction = 'none';
                
                // Trigger a canvas redraw to refresh event handlers
                const ctx = canvas.getContext('2d', { willReadFrequently: true });
                if (ctx) {
                  ctx.save();
                  ctx.restore();
                  // Force a minimal repaint
                  const imageData = ctx.getImageData(0, 0, 1, 1);
                  ctx.putImageData(imageData, 0, 0);
                }
              }
            });
          });
        }
      }, 200); // Increased delay to ensure modal drag state is fully cleared
      
      return () => clearTimeout(timer);
    }
  }, [modalOpen]);

  // Touch gesture utilities
  const getTouchDistance = (touches) => {
    if (touches.length < 2) return 0;
    const touch1 = touches[0];
    const touch2 = touches[1];
    return Math.sqrt(
      Math.pow(touch2.clientX - touch1.clientX, 2) +
      Math.pow(touch2.clientY - touch1.clientY, 2)
    );
  };

  const getTouchCenter = (touches) => {
    if (touches.length === 1) {
      return { x: touches[0].clientX, y: touches[0].clientY };
    }
    const touch1 = touches[0];
    const touch2 = touches[1];
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2
    };
  };

  const resetView = useCallback(() => {
    // Get canvas dimensions
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const canvasWidth = canvas.clientWidth;
    const canvasHeight = canvas.clientHeight;
    const centerX = canvasWidth / 2;
    const centerY = canvasHeight / 2; // Use actual center of viewport
    
    // Resume animation
    setIsAnimationPaused(false);
    
    // Clear focused node
    setFocusedNode(null);
    
    // Update center node position to current dynamic center
    setNodes(prevNodes => 
      prevNodes.map(node => 
        node.isCenter 
          ? { ...node, x: centerX, y: centerY }
          : node
      )
    );
    
    // Reset camera to show center node at center of view
    setCameraOffset({ x: 0, y: 0 });
    setZoom(1);
    setSelectedNode(null);
    setSearchQuery('');
    
    console.log('View reset: Animation resumed, focus cleared');
  }, []);

  const initializeCosmicLayout = () => {
    const cosmicNodes = [];
    
    // Get actual viewport dimensions instead of canvas dimensions
    const canvas = canvasRef.current;
    const canvasWidth = canvas ? canvas.clientWidth : window.innerWidth;
    const canvasHeight = canvas ? canvas.clientHeight : window.innerHeight;
    
    // Center position should be in the middle of the actual viewport
    const centerX = canvasWidth / 2;
    const centerY = canvasHeight / 2;    // Center node (current user) - positioned in the actual center of viewport
    cosmicNodes.push({
      id: 'center',
      type: 'user',
      name: 'You',
      avatar: null, // No emoji, will show 'Y' initial
      profileImage: null, // Avatar service disabled - using initials only
      x: centerX,
      y: centerY,
      radius: 40,
      color: 'from-purple-500 to-indigo-600',
      glowColor: '#8b5cf6',
      isCenter: true,
      xp: 3250,
      level: 15
    });

    // Close Circle - First orbit (closest)
    const closeOrbitRadius = 120;
    connections.closeCircle?.forEach((friend, index) => {
      // Ensure even distribution with small random offset to prevent perfect alignment
      const baseAngle = (index / connections.closeCircle.length) * 2 * Math.PI;
      const angleOffset = (Math.random() - 0.5) * 0.2; // Small random offset
      const angle = baseAngle + angleOffset;
      const orbitVariation = 15 + (Math.random() * 10); // 15-25px variation
        cosmicNodes.push({
        id: `close-${friend.id}`,
        type: 'close-friend',
        name: friend.name,
        avatar: null, // No emoji, will show name initial
        profileImage: friend.profileImage, // Add profile image
        x: centerX + Math.cos(angle) * (closeOrbitRadius + orbitVariation),
        y: centerY + Math.sin(angle) * (closeOrbitRadius + orbitVariation),
        radius: 30,
        color: 'from-pink-500 to-rose-600',
        glowColor: '#ec4899',
        xp: friend.xp,
        lastActive: friend.lastActive,
        compatibility: Math.floor(75 + Math.random() * 20), // 75-95%
        orbitRadius: closeOrbitRadius + orbitVariation,
        angle: angle,
        orbitSpeed: 0.0052 // 3 rotations per hour (360 degrees in 1200 seconds = 0.3 degrees/second = 0.0052 radians/second)
      });
    });

    // General Circle - Second orbit
    const generalOrbitRadius = 200;
    connections.generalCircle?.forEach((friend, index) => {
      // Offset by pi/4 and add spacing to avoid close circle overlap
      const baseAngle = (index / connections.generalCircle.length) * 2 * Math.PI + Math.PI / 4;
      const angleOffset = (Math.random() - 0.5) * 0.15; // Smaller random offset
      const angle = baseAngle + angleOffset;
      const orbitVariation = 20 + (Math.random() * 15); // 20-35px variation
        cosmicNodes.push({
        id: `general-${friend.id}`,
        type: 'general-friend',
        name: friend.name,
        avatar: null, // No emoji, will show name initial
        profileImage: friend.profileImage, // Add profile image
        x: centerX + Math.cos(angle) * (generalOrbitRadius + orbitVariation),
        y: centerY + Math.sin(angle) * (generalOrbitRadius + orbitVariation),
        radius: 25,
        color: 'from-blue-500 to-cyan-600',
        glowColor: '#3b82f6',
        xp: friend.xp,
        lastActive: friend.lastActive,
        compatibility: Math.floor(60 + Math.random() * 25), // 60-85%
        orbitRadius: generalOrbitRadius + orbitVariation,
        angle: angle,
        orbitSpeed: 0.0052 // 3 rotations per hour (360 degrees in 1200 seconds = 0.3 degrees/second = 0.0052 radians/second)
      });
    });

    // Private Groups - Third orbit (larger nodes)
    const groupOrbitRadius = 280;
    connections.privateGroups?.forEach((group, index) => {
      // Offset by pi/6 to avoid overlap with other orbits
      const baseAngle = (index / connections.privateGroups.length) * 2 * Math.PI + Math.PI / 6;
      const angleOffset = (Math.random() - 0.5) * 0.1; // Very small random offset
      const angle = baseAngle + angleOffset;
      const orbitVariation = 25 + (Math.random() * 10); // 25-35px variation
        cosmicNodes.push({
        id: `group-${group.id}`,
        type: 'group',
        name: group.name,
        avatar: null, // No emoji, will show group name initial
        x: centerX + Math.cos(angle) * (groupOrbitRadius + orbitVariation),
        y: centerY + Math.sin(angle) * (groupOrbitRadius + orbitVariation),
        radius: 35,
        color: 'from-emerald-500 to-teal-600',
        glowColor: '#10b981',
        members: group.members,
        membersList: group.membersList || [], // Grup üyelerinin listesi
        isExpanded: false,
        orbitRadius: groupOrbitRadius + orbitVariation,
        angle: angle,
        orbitSpeed: 0.0052 // 3 rotations per hour (360 degrees in 1200 seconds = 0.3 degrees/second = 0.0052 radians/second)
      });
    });

    setNodes(cosmicNodes);
  };

  // Animation state separate from nodes state
  const [animationTime, setAnimationTime] = useState(0);
  const startTimeRef = useRef(Date.now());
  const lastDrawRef = useRef(0);

  // Animation loop for orbital motion - using time-based animation with pause control
  useEffect(() => {
    let animationFrame;
    
    const animate = () => {
      const currentTime = Date.now();
      const elapsedTime = currentTime - startTimeRef.current;
      
      // Only update animation time if not paused
      if (!isAnimationPaused && currentTime - lastDrawRef.current > 33) {
        setAnimationTime(elapsedTime);
        lastDrawRef.current = currentTime;
      }
      
      animationFrame = requestAnimationFrame(animate);
    };
    
    animationFrame = requestAnimationFrame(animate);
    
    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [isAnimationPaused]); // Add pause state to dependencies
  
  // Calculate animated positions based on time (no state updates in animation loop)
  const getAnimatedPosition = useCallback((node) => {
    if (node.isCenter) return { x: node.x, y: node.y };
    
    // If animation is paused, return current static position
    if (isAnimationPaused) {
      return { x: node.x, y: node.y };
    }
    
    // Get current canvas dimensions for center calculation
    const canvas = canvasRef.current;
    const canvasWidth = canvas ? canvas.clientWidth : 800;
    const canvasHeight = canvas ? canvas.clientHeight : 600;
    const dynamicCenterX = canvasWidth / 2;
    const dynamicCenterY = canvasHeight / 2;
    
    // Handle group member nodes
    if (node.parentGroupId) {
      const parentGroup = nodes.find(n => n.id === node.parentGroupId);
      if (parentGroup) {
        // Get the parent group's animated position first
        const parentAnimatedPos = getAnimatedPosition(parentGroup);
        
        if (isFinite(parentAnimatedPos.x) && isFinite(parentAnimatedPos.y)) {
          const baseAngle = node.angle || 0;
          // Group members rotate around their parent group
          const animatedAngle = baseAngle + (animationTime * (2 * Math.PI) / 1200000); // 20 minutes per rotation
          const distance = node.orbitRadius || 80;
          
          if (isFinite(animatedAngle) && isFinite(distance)) {
            const x = parentAnimatedPos.x + Math.cos(animatedAngle) * distance;
            const y = parentAnimatedPos.y + Math.sin(animatedAngle) * distance;
            
            if (isFinite(x) && isFinite(y)) {
              return { x, y };
            }
          }
        }
      }
      return { x: node.x, y: node.y };
    }

    // Handle regular orbital nodes - always use current center node's position as reference
    if (node.orbitSpeed && isFinite(node.orbitSpeed)) {
      const baseAngle = node.angle || 0;
      // 3 rotations per hour (20 minutes per rotation = 1200 seconds = 1200000 ms)
      const animatedAngle = baseAngle + (animationTime * (2 * Math.PI) / 1200000);
      const orbitRadius = node.orbitRadius || 100;
      
      // Always use the center node's actual position as reference point
      const centerNode = nodes.find(n => n.isCenter);
      const centerX = centerNode ? centerNode.x : dynamicCenterX;
      const centerY = centerNode ? centerNode.y : dynamicCenterY;
      
      if (isFinite(animatedAngle) && isFinite(orbitRadius)) {
        const x = centerX + Math.cos(animatedAngle) * orbitRadius;
        const y = centerY + Math.sin(animatedAngle) * orbitRadius;        
        if (isFinite(x) && isFinite(y)) {
          return { x, y };
        }
      }
    }
    
    return { x: node.x, y: node.y };
  }, [nodes, animationTime, isAnimationPaused]);  // Camera tracking effect disabled - using direct positioning for search
  // useEffect(() => {
  //   if (!selectedNode || !canvasRef.current) return;
  //   ...tracking logic removed  // }, [selectedNode, getAnimatedPosition]);
  // Helper function to center camera on a specific node - moved before handleSearch
  const centerCameraOnNode = useCallback((node) => {
    const canvas = canvasRef.current;
    if (!canvas || !node) return;
    
    const canvasWidth = canvas.clientWidth;
    const canvasHeight = canvas.clientHeight;
    const viewportCenterX = canvasWidth / 2;
    const viewportCenterY = canvasHeight / 2;
    
    // Use the node's current static position
    const nodePosition = getAnimatedPosition(node);
    
    // Calculate offset to center the node in viewport
    const offset = {
      x: viewportCenterX - nodePosition.x,
      y: viewportCenterY - nodePosition.y
    };
      console.log(`Search: Centering camera on "${node.name}" at position (${nodePosition.x}, ${nodePosition.y})`);
    
    // Set camera offset and zoom for better focus
    setCameraOffset(offset);
    setZoom(1.04); // Zoom in slightly for better focus
    
    // Pause animation briefly to ensure smooth centering
    setIsAnimationPaused(true);
    setTimeout(() => {
      setIsAnimationPaused(false);
    }, 500);
  }, [getAnimatedPosition]);

  // Shared click handler for both mouse and touch
  const handleNodeClick = useCallback((node) => {
    console.log(`Click action on: ${node.name}`);
    
    if (node.type === 'group') {
      // Toggle group expansion
      console.log(`Toggling group expansion for: ${node.name}`);
      
      setNodes(prevNodes => {
        let updatedNodes = [...prevNodes];
        
        // First, collapse all other expanded groups and remove their members
        updatedNodes = updatedNodes.map(n => {
          if (n.type === 'group' && n.id !== node.id && n.isExpanded) {
            console.log(`Collapsing other group: ${n.name}`);
            return { ...n, isExpanded: false };
          }
          return n;
        }).filter(n => {
          // Remove member nodes of other groups
          if (n.type === 'group-member' && n.parentGroupId !== node.id) {
            return false;
          }
          return true;
        });
        
        // Now handle the clicked group
        const targetGroup = updatedNodes.find(n => n.id === node.id);
        if (!targetGroup) return updatedNodes;
        
        const newExpandedState = !targetGroup.isExpanded;
        console.log(`Group ${targetGroup.name} expanded: ${newExpandedState}`);
        
        // Update the target group's expanded state
        updatedNodes = updatedNodes.map(n => {
          if (n.id === node.id) {
            return { ...n, isExpanded: newExpandedState };
          }
          return n;
        });
        
        // If expanding, add member nodes
        if (newExpandedState && targetGroup.membersList) {
          console.log(`Adding ${targetGroup.membersList.length} members for group ${targetGroup.name}`);
          
          targetGroup.membersList.forEach((member, index) => {
            const memberAngle = (index / targetGroup.membersList.length) * 2 * Math.PI;
            const memberDistance = 60; // Distance from group center
            
            const memberNode = {
              id: `member-${targetGroup.id}-${member.id}`,
              type: 'group-member',
              name: member.name,
              avatar: null,
              profileImage: member.profileImage,
              x: targetGroup.x + Math.cos(memberAngle) * memberDistance,
              y: targetGroup.y + Math.sin(memberAngle) * memberDistance,
              radius: 20,
              color: 'from-amber-500 to-yellow-600',
              glowColor: '#f59e0b',
              xp: member.xp,
              level: member.level,
              lastActive: member.lastActive,
              isOnline: member.isOnline,
              parentGroupId: targetGroup.id,
              angle: memberAngle,
              orbitRadius: memberDistance,
              orbitSpeed: 0.008 // Slightly faster rotation for members
            };
            
            updatedNodes.push(memberNode);
          });
        }
        
        return updatedNodes;
      });
    } else {
      // For users and group members, collapse all groups first, then show profile
      console.log('Showing profile for:', node.name);
      
      // Collapse all expanded groups when clicking on a user or group member
      setNodes(prevNodes => {
        return prevNodes.map(n => {
          if (n.type === 'group' && n.isExpanded) {
            console.log(`Collapsing group when showing profile: ${n.name}`);
            return { ...n, isExpanded: false };
          }
          return n;
        }).filter(n => {
          // Remove all member nodes
          return n.type !== 'group-member';
        });
      });
      
      if (onUserClick) {
        // Convert node data to user format expected by UserDetailModal
        const userData = {
          id: node.id,
          name: node.name,
          avatar: node.name.charAt(0).toUpperCase(),
          profileImage: node.profileImage,
          xp: node.xp || 1000,
          level: node.level || Math.floor((node.xp || 1000) / 200) + 1,
          lastActive: node.lastActive || 'Online',
          isOnline: node.isOnline !== false,
          bio: node.bio || 'Member of your cosmic network',
          type: node.type,
          compatibility: node.compatibility || 85
        };
        console.log('CosmicConnectionsMap: Calling onUserClick with data:', userData);
        onUserClick(userData);
      } else {
        console.log('CosmicConnectionsMap: onUserClick prop is not available');
      }
    }
  }, [onUserClick]);

  // Search functionality - memoized to prevent re-creation
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
    setSelectedNode(null); // Clear any previous selection first
    
    if (query.trim()) {
      const searchTerm = query.toLowerCase().trim();
      console.log(`Search: Looking for "${searchTerm}"`);
      
      // First check for direct node matches (users and groups)
      let directMatch = nodes.find(node => {
        // Exact name match (highest priority)
        if (node.name.toLowerCase() === searchTerm) return true;
        // Name starts with search term
        if (node.name.toLowerCase().startsWith(searchTerm)) return true;
        // Name contains search term
        if (node.name.toLowerCase().includes(searchTerm)) return true;
        return false;
      });

      // If no direct match, check for group member matches
      let groupWithMember = null;
      let memberMatch = null;
      
      if (!directMatch) {
        for (const node of nodes) {
          if (node.type === 'group' && node.membersList) {
            const member = node.membersList.find(member => 
              member.name.toLowerCase() === searchTerm ||
              member.name.toLowerCase().startsWith(searchTerm) ||
              member.name.toLowerCase().includes(searchTerm)
            );
            
            if (member) {
              groupWithMember = node;
              memberMatch = member;
              console.log(`Search: Found member "${member.name}" in group "${node.name}"`);
              break;
            }
          }
        }
      }

      // Handle direct node match (user or group)
      if (directMatch) {
        console.log(`Search: Direct match found "${directMatch.name}"`);
          // If it's a group, expand it
        if (directMatch.type === 'group' && !directMatch.isExpanded) {
          console.log(`Search: Expanding group "${directMatch.name}"`);
          handleNodeClick(directMatch);
        }
        
        // Center camera on the node
        setSelectedNode(directMatch);
        setFocusedNode(directMatch);
        centerCameraOnNode(directMatch);
        
      // Handle group member match
      } else if (groupWithMember && memberMatch) {
        console.log(`Search: Expanding group "${groupWithMember.name}" to show member "${memberMatch.name}"`);
        
        // First expand the group if not already expanded
        if (!groupWithMember.isExpanded) {
          handleNodeClick(groupWithMember);
        }
        
        // Wait for group expansion to complete, then find and center on member
        setTimeout(() => {
          setNodes(currentNodes => {
            const memberNodeId = `member-${groupWithMember.id}-${memberMatch.id}`;
            const memberNode = currentNodes.find(n => n.id === memberNodeId);
            
            if (memberNode) {
              console.log(`Search: Centering on member "${memberMatch.name}"`);
              setSelectedNode(memberNode);
              setFocusedNode(memberNode);
              centerCameraOnNode(memberNode);
            } else {
              console.log(`Search: Member node not found, centering on group instead`);
              setSelectedNode(groupWithMember);
              setFocusedNode(groupWithMember);
              centerCameraOnNode(groupWithMember);
            }
            
            return currentNodes;          });
        }, 100); // Short delay to allow group expansion
        
      } else {
        console.log(`Search: No matches found for "${searchTerm}"`);
      }
    }
  }, [nodes, handleNodeClick, centerCameraOnNode]);

  // Canvas drawing function
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const rect = canvas.getBoundingClientRect();
    
    // Set canvas size
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    
    // Clear canvas
    ctx.fillStyle = currentTheme.bg === 'bg-gray-900' ? '#111827' : '#f9fafb';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Transform for camera
    ctx.save();
    ctx.translate(cameraOffset.x, cameraOffset.y);
    ctx.scale(zoom, zoom);
    
    // Draw cosmic background effects
    drawCosmicBackground(ctx);
    
    // Draw orbital paths
    drawOrbitalPaths(ctx);
    
    // Draw connection lines
    drawConnections(ctx);
    
    // Draw nodes with animated positions
    nodes.forEach(node => {
      const animatedPos = getAnimatedPosition(node);
      drawNode(ctx, { ...node, x: animatedPos.x, y: animatedPos.y });
    });
    
    ctx.restore();
  }, [nodes, hoveredNode, currentTheme, cameraOffset, zoom, animationTime, getAnimatedPosition]);

  const drawCosmicBackground = (ctx) => {
    // Get current viewport bounds including camera offset and zoom
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const canvasWidth = canvas.clientWidth;
    const canvasHeight = canvas.clientHeight;
    
    // Calculate visible area bounds considering camera offset and zoom
    const visibleLeft = -cameraOffset.x / zoom - canvasWidth;
    const visibleRight = -cameraOffset.x / zoom + canvasWidth * 2;
    const visibleTop = -cameraOffset.y / zoom - canvasHeight;
    const visibleBottom = -cameraOffset.y / zoom + canvasHeight * 2;
    
    // Grid spacing
    const gridSpacing = 50;
    
    // Draw grid lines that extend beyond visible area
    ctx.strokeStyle = currentTheme.bg === 'bg-gray-900' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)';
    ctx.lineWidth = 1;
    
    // Calculate grid line positions to cover visible area plus margins
    const startX = Math.floor(visibleLeft / gridSpacing) * gridSpacing;
    const endX = Math.ceil(visibleRight / gridSpacing) * gridSpacing;
    const startY = Math.floor(visibleTop / gridSpacing) * gridSpacing;
    const endY = Math.ceil(visibleBottom / gridSpacing) * gridSpacing;
    
    // Draw vertical lines
    for (let x = startX; x <= endX; x += gridSpacing) {
      ctx.beginPath();
      ctx.moveTo(x, startY);
      ctx.lineTo(x, endY);
      ctx.stroke();
    }
    
    // Draw horizontal lines
    for (let y = startY; y <= endY; y += gridSpacing) {
      ctx.beginPath();
      ctx.moveTo(startX, y);
      ctx.lineTo(endX, y);
      ctx.stroke();
    }
  };

  const drawOrbitalPaths = (ctx) => {
    // Get center node position
    const centerNode = nodes.find(node => node.isCenter);
    if (!centerNode) return;
    
    const centerX = centerNode.x;
    const centerY = centerNode.y;
    
    // Define orbit radii
    const orbits = [120, 200, 280];
    
    orbits.forEach(radius => {
      ctx.strokeStyle = currentTheme.bg === 'bg-gray-900' 
        ? 'rgba(255,255,255,0.08)' 
        : 'rgba(0,0,0,0.08)';
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);
      
      ctx.beginPath();
      ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
      ctx.stroke();
      
      ctx.setLineDash([]);
    });
  };
  const drawConnections = (ctx) => {
    const centerNode = nodes.find(node => node.isCenter);
    if (!centerNode) return;
    
    nodes.forEach(node => {
      if (node.isCenter) return;
      
      const animatedPos = getAnimatedPosition(node);
      
      // Handle group member connections differently
      if (node.type === 'group-member') {
        // Connect group members to their parent group, not center
        const parentGroup = nodes.find(n => n.id === node.parentGroupId);
        if (parentGroup) {
          const parentPos = getAnimatedPosition(parentGroup);
          
          const memberGradient = ctx.createLinearGradient(
            parentPos.x, parentPos.y,
            animatedPos.x, animatedPos.y
          );
          
          memberGradient.addColorStop(0, 'rgba(245, 158, 11, 0.8)');
          memberGradient.addColorStop(1, 'rgba(245, 158, 11, 0.2)');
          
          ctx.strokeStyle = memberGradient;
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(parentPos.x, parentPos.y);
          ctx.lineTo(animatedPos.x, animatedPos.y);
          ctx.stroke();
        }
        return;
      }
      
      // Create gradient line from center to node for regular nodes
      const gradient = ctx.createLinearGradient(
        centerNode.x, centerNode.y,
        animatedPos.x, animatedPos.y
      );
      
      // Connection color based on node type
      let startColor, endColor;
      if (node.type === 'close-friend') {
        startColor = 'rgba(236, 72, 153, 0.6)';
        endColor = 'rgba(236, 72, 153, 0.1)';
      } else if (node.type === 'general-friend') {
        startColor = 'rgba(59, 130, 246, 0.4)';
        endColor = 'rgba(59, 130, 246, 0.05)';
      } else if (node.type === 'group') {
        startColor = 'rgba(16, 185, 129, 0.5)';
        endColor = 'rgba(16, 185, 129, 0.1)';
      } else {
        startColor = 'rgba(255, 255, 255, 0.3)';
        endColor = 'rgba(255, 255, 255, 0.05)';
      }
      
      gradient.addColorStop(0, startColor);
      gradient.addColorStop(1, endColor);
      
      ctx.strokeStyle = gradient;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(centerNode.x, centerNode.y);
      ctx.lineTo(animatedPos.x, animatedPos.y);
      ctx.stroke();
    });
  };

  const drawNode = (ctx, node) => {
    const { x, y, radius, color, glowColor, type, name, avatar } = node;
    const isSelected = selectedNode?.id === node.id;
    const isHovered = hoveredNode?.id === node.id;
    
    // Enhanced glow for selected/hovered nodes
    if (isSelected || isHovered) {
      const glowRadius = radius * (isSelected ? 3 : 2);
      const glowGradient = ctx.createRadialGradient(x, y, radius, x, y, glowRadius);
      
      if (isSelected) {
        glowGradient.addColorStop(0, 'rgba(236, 72, 153, 0.8)');
        glowGradient.addColorStop(0.5, 'rgba(236, 72, 153, 0.4)');
        glowGradient.addColorStop(1, 'rgba(236, 72, 153, 0)');
      } else {
        glowGradient.addColorStop(0, glowColor || 'rgba(255, 255, 255, 0.5)');
        glowGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      }
      
      ctx.fillStyle = glowGradient;
      ctx.beginPath();
      ctx.arc(x, y, glowRadius, 0, Math.PI * 2);
      ctx.fill();
    }
      // Regular glow - reduced intensity
    if (glowColor && !isSelected && !isHovered) {
      const normalGlow = ctx.createRadialGradient(x, y, radius, x, y, radius * 1.3);
      normalGlow.addColorStop(0, glowColor.replace('0.8', '0.2').replace('0.6', '0.15'));
      normalGlow.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.fillStyle = normalGlow;
      ctx.beginPath();
      ctx.arc(x, y, radius * 1.3, 0, Math.PI * 2);
      ctx.fill();
    }
    
    // Node circle with gradient
    let nodeGradient;
    if (color.includes('from-') && color.includes('to-')) {
      // Parse Tailwind gradient classes
      nodeGradient = ctx.createLinearGradient(x - radius, y - radius, x + radius, y + radius);
      
      if (color.includes('purple') && color.includes('indigo')) {
        nodeGradient.addColorStop(0, '#8b5cf6');
        nodeGradient.addColorStop(1, '#6366f1');
      } else if (color.includes('pink') && color.includes('rose')) {
        nodeGradient.addColorStop(0, '#ec4899');
        nodeGradient.addColorStop(1, '#f43f5e');
      } else if (color.includes('blue') && color.includes('cyan')) {
        nodeGradient.addColorStop(0, '#3b82f6');
        nodeGradient.addColorStop(1, '#06b6d4');
      } else if (color.includes('emerald') && color.includes('teal')) {
        nodeGradient.addColorStop(0, '#10b981');
        nodeGradient.addColorStop(1, '#14b8a6');
      } else {
        nodeGradient.addColorStop(0, '#6b7280');
        nodeGradient.addColorStop(1, '#4b5563');
      }
    } else {
      nodeGradient = color;
    }
    
    ctx.fillStyle = nodeGradient;
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2);
    ctx.fill();
    
    // Selected node border
    if (isSelected) {
      ctx.strokeStyle = '#ec4899';
      ctx.lineWidth = 3;
      ctx.setLineDash([8, 4]);
      ctx.beginPath();
      ctx.arc(x, y, radius + 8, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
    }    // Profile image or initial display
    ctx.fillStyle = 'white';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Check if node has profileImage
    if (node.profileImage) {
      // Try to draw profile image (implementation for when images are available)
      // For now, fall back to initials since profileImage support is not implemented
      const initial = name.charAt(0).toUpperCase();
      ctx.font = `bold ${Math.max(14, radius * 0.7)}px Arial`;
      ctx.fillText(initial, x, y);
    } else {
      // Show first letter of name as initial
      const initial = name.charAt(0).toUpperCase();
      ctx.font = `bold ${Math.max(14, radius * 0.7)}px Arial`;
      ctx.fillText(initial, x, y);
    }
    
    // Node label - always show name below node
    ctx.fillStyle = currentTheme.bg === 'bg-gray-900' ? 'white' : 'black';
    ctx.font = `${Math.max(10, Math.min(14, radius * 0.4))}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    
    // Add background for better readability
    const labelY = y + radius + 8;
    const textMetrics = ctx.measureText(name);
    const labelWidth = textMetrics.width + 8;
    const labelHeight = 16;
    
    // Semi-transparent background for text
    ctx.fillStyle = currentTheme.bg === 'bg-gray-900' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.8)';
    ctx.fillRect(x - labelWidth/2, labelY - 2, labelWidth, labelHeight);
    
    // Draw text
    ctx.fillStyle = currentTheme.bg === 'bg-gray-900' ? 'white' : 'black';
    ctx.fillText(name, x, labelY + 2);
  };

  // Mouse and touch event handlers
  const getMousePos = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    return {
      x: (e.clientX - rect.left) * scaleX / window.devicePixelRatio,
      y: (e.clientY - rect.top) * scaleY / window.devicePixelRatio
    };
  }, []);

  const getNodeAtPosition = useCallback((x, y) => {
    // Transform mouse coordinates to world coordinates
    const worldX = (x - cameraOffset.x) / zoom;
    const worldY = (y - cameraOffset.y) / zoom;
    
    for (const node of nodes) {
      const animatedPos = getAnimatedPosition(node);
      const distance = Math.sqrt(
        Math.pow(worldX - animatedPos.x, 2) + 
        Math.pow(worldY - animatedPos.y, 2)
      );
      
      if (distance <= node.radius) {
        return node;
      }    }
    return null;
  }, [nodes, cameraOffset, zoom, getAnimatedPosition]);  const handleMouseDown = useCallback((e) => {
    if (modalOpen) return;
    
    const mousePos = getMousePos(e);
    const clickedNodeAtPos = getNodeAtPosition(mousePos.x, mousePos.y);
    
    if (clickedNodeAtPos && !clickedNodeAtPos.isCenter) {
      // Store the clicked node but don't perform any actions yet
      setClickedNode(clickedNodeAtPos);
      setSelectedNode(clickedNodeAtPos);
      console.log(`Mouse down on: ${clickedNodeAtPos.name}`);
    } else {
      // Start panning on empty space, but also store that we clicked empty space
      setIsPanning(true);  
      setPanStart({ x: mousePos.x, y: mousePos.y });
      setSelectedNode(null);
      setClickedNode('empty-space'); // Special marker for empty space click
    }
  }, [modalOpen, getMousePos, getNodeAtPosition]);  const handleMouseMove = useCallback((e) => {
    if (modalOpen) return;
    
    const mousePos = getMousePos(e);
    
    if (isPanning) {
      const deltaX = mousePos.x - panStart.x;
      const deltaY = mousePos.y - panStart.y;
      
      // Check if we've moved enough to consider this a drag
      const dragDistance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      if (dragDistance > dragThreshold) {
        // This is a drag, clear any clicked node (including empty-space marker)
        setClickedNode(null);
      }
      
      setCameraOffset(prev => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY
      }));
      
      setPanStart({ x: mousePos.x, y: mousePos.y });
    } else {
      // Hover detection
      const hoveredNode = getNodeAtPosition(mousePos.x, mousePos.y);
      setHoveredNode(hoveredNode);
    }  }, [modalOpen, getMousePos, isPanning, panStart, getNodeAtPosition, dragThreshold]);
const handleMouseUp = useCallback(() => {
    setIsPanning(false);
    
    // Perform action based on what was clicked
    if (clickedNode && clickedNode !== 'empty-space') {
      // Normal node click
      handleNodeClick(clickedNode);
    } else if (clickedNode === 'empty-space') {
      // Empty space click - collapse all expanded groups
      console.log('Empty space click - collapsing all expanded groups');
      setNodes(prevNodes => {
        return prevNodes.map(node => {
          if (node.type === 'group' && node.isExpanded) {
            console.log(`Collapsing group from empty space click: ${node.name}`);
            return { ...node, isExpanded: false };
          }
          return node;
        }).filter(node => {
          // Remove all member nodes
          return node.type !== 'group-member';
        });
      });
    }
    
    // Clear clicked node after action
    setClickedNode(null);
  }, [clickedNode, handleNodeClick]);
  const handleWheel = useCallback((e) => {
    if (modalOpen) return;
    
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom(prev => Math.max(0.5, Math.min(4, prev * delta)));  }, [modalOpen]);
  
  // Touch event handlers
  const handleTouchStart = useCallback((e) => {
    if (modalOpen) return;
    
    if (e.touches.length === 1) {
      const touch = e.touches[0];
      const touchPos = getMousePos(touch);
      const touchedNode = getNodeAtPosition(touchPos.x, touchPos.y);
      
      if (touchedNode && !touchedNode.isCenter) {
        // Store the touched node but don't perform action yet
        setClickedNode(touchedNode);
        setSelectedNode(touchedNode);
        console.log(`Touch start on: ${touchedNode.name}`);
      } else {
        // Touching empty space - start panning and mark as empty space touch
        setIsPanning(true);
        setPanStart({ x: touchPos.x, y: touchPos.y });
        setClickedNode('empty-space'); // Special marker for empty space touch
      }
    } else if (e.touches.length === 2) {
      setIsPanning(false);
      setLastTouchDistance(getTouchDistance(e.touches));
      setClickedNode(null);
    }
  }, [modalOpen, getMousePos, getNodeAtPosition, getTouchDistance]);
    const handleTouchMove = useCallback((e) => {
    if (modalOpen) return;
    
    // Remove preventDefault to avoid passive listener error
    
    if (e.touches.length === 1 && isPanning) {
      const touch = e.touches[0];
      const touchPos = getMousePos(touch);
      const deltaX = touchPos.x - panStart.x;
      const deltaY = touchPos.y - panStart.y;
      
      // Check if we've moved enough to consider this a drag
      const dragDistance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      if (dragDistance > dragThreshold) {
        // This is a drag, clear any clicked node (including empty-space marker)
        setClickedNode(null);
      }
      
      setCameraOffset(prev => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY
      }));
      
      setPanStart({ x: touchPos.x, y: touchPos.y });
    } else if (e.touches.length === 2) {
      const currentDistance = getTouchDistance(e.touches);
      const delta = currentDistance / lastTouchDistance;
      
      setZoom(prev => Math.max(0.5, Math.min(4, prev * delta)));
      setLastTouchDistance(currentDistance);
      
      // Clear clicked node during zoom
      setClickedNode(null);
    }
  }, [modalOpen, isPanning, panStart, getMousePos, getTouchDistance, lastTouchDistance, dragThreshold]);  const handleTouchEnd = useCallback(() => {
    setIsPanning(false);
    setLastTouchDistance(0);
    
    // Perform action based on what was touched
    if (clickedNode && clickedNode !== 'empty-space') {
      // Normal node touch
      handleNodeClick(clickedNode);
    } else if (clickedNode === 'empty-space') {
      // Empty space touch - collapse all expanded groups
      console.log('Empty space touch - collapsing all expanded groups');
      setNodes(prevNodes => {
        return prevNodes.map(node => {
          if (node.type === 'group' && node.isExpanded) {
            console.log(`Collapsing group from empty space touch: ${node.name}`);
            return { ...node, isExpanded: false };
          }
          return node;
        }).filter(node => {
          // Remove all member nodes
          return node.type !== 'group-member';
        });
      });
    }
    
    // Clear clicked node after action
    setClickedNode(null);
  }, [clickedNode, handleNodeClick]);

  // Search handlers
  const handleSearchClick = useCallback((e) => {
    e.stopPropagation();
    setShowSearch(true);
  }, []);

  const handleSearchClose = useCallback(() => {
    setShowSearch(false);
    setSearchQuery(''); // Clear search query when modal closes
    setSelectedNode(null); // Clear any selected node to stop tracking
  }, []);

  // Zoom handlers
  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(4, prev * 1.2));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => Math.max(0.5, prev / 1.2));
  }, []);

  return (
    <div 
      ref={containerRef}
      className="relative w-full h-screen overflow-hidden bg-gray-900"
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}
    >
      {/* Canvas */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full cursor-grab active:cursor-grabbing"
        style={{ 
          width: '100%', 
          height: '100%',
          touchAction: 'none',
          userSelect: 'none'
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onWheel={handleWheel}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      />

      {/* Bottom Toolbar */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-40">
        <div className="flex items-center space-x-4 bg-slate-800/90 backdrop-blur-xl rounded-2xl px-6 py-3 border border-white/20 shadow-2xl">
          {/* Search Button */}
          <motion.button
            onClick={handleSearchClick}
            className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-xl hover:shadow-lg transition-all"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Search size={16} />
            <span className="font-medium">Search</span>
          </motion.button>

          {/* Zoom Controls */}
          <div className="flex items-center space-x-2">
            <motion.button
              onClick={handleZoomOut}
              className="p-2 bg-slate-700/80 text-white rounded-lg hover:bg-slate-600/80 transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <ZoomOut size={16} />
            </motion.button>
            
            <span className="text-white text-sm font-medium min-w-[3rem] text-center">
              {Math.round(zoom * 100)}%
            </span>
            
            <motion.button
              onClick={handleZoomIn}
              className="p-2 bg-slate-700/80 text-white rounded-lg hover:bg-slate-600/80 transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <ZoomIn size={16} />
            </motion.button>
          </div>

          {/* Home/Reset Button */}
          <motion.button
            onClick={resetView}
            className="p-2 bg-slate-700/80 text-white rounded-lg hover:bg-slate-600/80 transition-colors"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <Home size={16} />
          </motion.button>
        </div>
      </div>

      {/* Search Modal */}
      <AnimatePresence>
        <SearchModal
          currentTheme={currentTheme}
          onClose={handleSearchClose}
          onSearch={handleSearch}
          t={t}
          showSearch={showSearch}
          nodes={nodes}
        />
      </AnimatePresence>
    </div>
  );
};

export default CosmicConnectionsMap;